import { useSelector } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import GridHeader from '../components/GridHeader'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { formatNameDeroga, computeQuoziente } from '../hooks/utils-functions'
import {
  useGetClassificheQuery,
  useGetImpostazioniAvanzateQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import { getCategorie, getGironi } from '../hooks/selection-get-functions'

const AVANZ_FIELDS = ['IgnoraRisultatiSquadreFuoriClassifica']

const Classifiche = () => {
  const { admin } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCampionatoCalendariPubblicati = useSelector(
    state => state.selection.selectedCampionatoCalendariPubblicati,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const availableGironi = useSelector(state => state.selection.availableGironi)
  const selectedGirone = useSelector(state => state.selection.selectedGirone)
  const selectedTeam = useSelector(state => state.selection.selectedTeam)

  const skipQueries = !(
    selectedCampionato &&
    (admin || selectedCampionatoCalendariPubblicati)
  )

  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: skipQueries },
  )
  const { data: classifiche, isLoading } = useGetClassificheQuery(
    selectedCampionato,
    { skip: skipQueries },
  )

  document.title = `${config?.name} - Classifiche`

  const avulsa = 'avulsa'
  const isAvulsa = selectedGirone === avulsa
  const { IgnoraRisultatiSquadreFuoriClassifica } = avanzate || {}

  let columnsClassifiche
  if (isAvulsa) {
    columnsClassifiche = [
      { label: 'Squadra', sortable: false },
      { label: 'Classificata', sortable: false },
      { label: 'Punti/Partita', sortable: false },
      { label: 'Vittorie', sortable: false },
      { label: 'Quoziente set', sortable: false },
      { label: 'Quoziente punti', sortable: false },
    ]
  } else {
    columnsClassifiche = [
      { label: 'Squadra', sortable: false },
      { label: 'Punti', sortable: false },
      { label: 'Partite giocate', sortable: false },
      { label: 'Partite vinte', sortable: false },
      { label: 'Set vinti', sortable: false },
      { label: 'Set persi', sortable: false },
      { label: 'Quoziente set', sortable: false },
      { label: 'Quoziente punti', sortable: false },
      { label: 'Penalità', sortable: false },
    ]
  }

  const ClassificheTable = ({
    campionato = selectedCampionato,
    categoria,
    girone,
  }) => {
    const classificaTable = classifiche.find(
      i =>
        i.codiceCampionato === campionato &&
        i.categoria === categoria &&
        i.girone === girone,
    )?.squadre

    const classificaTitle =
      girone === avulsa ? 'Classifica avulsa' : `Girone ${girone}`

    return (
      <Grid size={12}>
        <h4>
          Categoria {categoria} - {classificaTitle}
        </h4>
        <TableContainer component={Paper}>
          <Table size='small'>
            <SortableTableHead
              table={classificaTable}
              columns={columnsClassifiche}
            />
            <TableBody>
              {classificaTable?.map((s, index) => {
                const sInfo = iscrizioniLight?.find(n => n.id === s.squadraID)
                const isSelectedTeam = s.squadraID === selectedTeam

                const sRanking = classifiche
                  ?.find(
                    i =>
                      i.codiceCampionato === campionato &&
                      i.categoria === categoria &&
                      i.girone === avulsa,
                  )
                  ?.squadre?.find(i => i.squadraID === s.squadraID)?.ranking

                const sPunti = (s.punteggio || 0) - (s.penalita || 0)

                const sPartite =
                  (s.partiteVinte || 0) +
                  (s.partitePerse || 0) +
                  (s.partitePareggiate || 0)

                return (
                  <TableRow
                    key={index}
                    style={{
                      backgroundColor:
                        (sRanking === 1 && colours.highlightGreen) ||
                        (sRanking === 2 && colours.highlightBlue) ||
                        colours.white,
                    }}
                  >
                    {isAvulsa ? (
                      <>
                        <TableCell>
                          {formatNameDeroga(sInfo, isSelectedTeam)}
                        </TableCell>
                        <TableCell>
                          {(sInfo?.hasDeroga && '-') ||
                            `${sRanking}° (girone ${sInfo?.girone})`}
                        </TableCell>
                        <TableCell>
                          <b>{computeQuoziente(sPunti, sPartite)}</b>
                        </TableCell>
                        <TableCell>
                          {Math.round(
                            Number(computeQuoziente(s.partiteVinte, sPartite)) *
                              100,
                          )}
                          %
                        </TableCell>
                        <TableCell>
                          {computeQuoziente(s.setVinti, s.setPersi)}
                        </TableCell>
                        <TableCell>
                          {computeQuoziente(s.puntiVinti, s.puntiPersi)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          {formatNameDeroga(sInfo, isSelectedTeam)}
                        </TableCell>
                        <TableCell>
                          <b>{sPunti}</b>
                        </TableCell>
                        <TableCell>{sPartite}</TableCell>
                        <TableCell>{s.partiteVinte || 0}</TableCell>
                        <TableCell>{s.setVinti || 0}</TableCell>
                        <TableCell>{s.setPersi || 0}</TableCell>
                        <TableCell>
                          {computeQuoziente(s.setVinti, s.setPersi)}
                        </TableCell>
                        <TableCell>
                          {computeQuoziente(s.puntiVinti, s.puntiPersi)}
                        </TableCell>
                        <TableCell>{s.penalita || 0}</TableCell>
                      </>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    )
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Classifiche' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar data={classifiche} yCampionato yCategoria yGirone yTeam />
      </Grid>
      {(!admin &&
        selectedCampionato &&
        !selectedCampionatoCalendariPubblicati && (
          <GridHeader text='Classifiche non ancora pubblicate.' />
        )) || (
        <>
          {(isLoading && <GridCircularProgress />) ||
            (selectedCategoria &&
              selectedGirone &&
              !classifiche?.filter(
                i =>
                  i.codiceCampionato === selectedCampionato &&
                  (selectedCategoria === availableCategorie[0]
                    ? true
                    : i.categoria === selectedCategoria) &&
                  (selectedGirone === availableGironi[0]
                    ? true
                    : i.girone === selectedGirone),
              )?.length && (
                <GridHeader text='Nessuna classifica da visualizzare.' />
              )) ||
            (classifiche?.length > 0 && !!selectedCampionato && (
              <>
                {getCategorie(
                  classifiche.filter(
                    i =>
                      i.codiceCampionato === selectedCampionato &&
                      (selectedCategoria === availableCategorie[0]
                        ? true
                        : i.categoria === selectedCategoria),
                  ),
                ).some(
                  categoria =>
                    getGironi(
                      classifiche.filter(
                        i =>
                          i.codiceCampionato === selectedCampionato &&
                          i.categoria === categoria &&
                          (selectedGirone === availableGironi[0]
                            ? i.girone !== avulsa
                            : i.girone === selectedGirone),
                      ),
                    )?.length > 0,
                ) && (
                  <Grid size={12}>
                    {selectedGirone === 'avulsa' &&
                      IgnoraRisultatiSquadreFuoriClassifica &&
                      'Le gare giocate contro squadre fuori classifica non contano nella classifica avulsa.'}
                    {selectedGirone !== 'avulsa' &&
                      'Le squadre con il punto esclamativo rosso sono fuori classifica e non passano alle fasi finali.'}
                    {selectedGirone !== 'avulsa' &&
                      IgnoraRisultatiSquadreFuoriClassifica && (
                        <>
                          <br />
                          Le gare giocate contro le squadre fuori classifica non
                          contano nella classifica avulsa.
                        </>
                      )}
                  </Grid>
                )}
                <Grid container spacing={3} sx={{ width: '100%' }}>
                  {getCategorie(
                    classifiche.filter(
                      i =>
                        i.codiceCampionato === selectedCampionato &&
                        (selectedCategoria === availableCategorie[0]
                          ? true
                          : i.categoria === selectedCategoria),
                    ),
                  ).map(categoria =>
                    getGironi(
                      classifiche.filter(
                        i =>
                          i.codiceCampionato === selectedCampionato &&
                          i.categoria === categoria &&
                          (selectedGirone === availableGironi[0]
                            ? i.girone !== avulsa
                            : i.girone === selectedGirone),
                      ),
                    ).map(girone => (
                      <Grid
                        key={`${selectedCampionato}-${categoria}-${girone}`}
                        size={12}
                      >
                        <ClassificheTable
                          campionato={selectedCampionato}
                          categoria={categoria}
                          girone={girone}
                        />
                      </Grid>
                    )),
                  )}
                </Grid>
              </>
            ))}
        </>
      )}
    </Grid>
  )
}

export default Classifiche
